import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { EarningsResponse } from "../../../interfaces/earnings";
import { useHttpClient } from "../../../hooks/useHttpClient";
import { useLocalization } from "../../../stores/localizationContext";
import { useUserContext } from "../../../stores/userContext";
import { Dropdown } from "../../Common/Dropdown/Dropdown";
import { deriveScheduleString } from "../../../utils/earningsUtils";
import "./Earnings.scss";

// ── Helpers ──────────────────────────────────────────────────────────────────

function getCurrentAcademicYear(): string {
  const now = new Date();
  const year = now.getFullYear() % 100; // 2-digit year
  const month = now.getMonth() + 1; // 1-indexed
  return month >= 9
    ? `${year}-${year + 1}`
    : `${year - 1}-${year}`;
}

function getAvailableAcademicYears(): string[] {
  const now = new Date();
  const year = now.getFullYear() % 100; // 2-digit year
  const month = now.getMonth() + 1;
  const currentStartYear = month >= 9 ? year : year - 1;
  return [
    `${currentStartYear + 1}-${currentStartYear + 2}`,
    `${currentStartYear}-${currentStartYear + 1}`,
    `${currentStartYear - 1}-${currentStartYear}`,
    `${currentStartYear - 2}-${currentStartYear - 1}`,
    `${currentStartYear - 3}-${currentStartYear - 2}`,
    `${currentStartYear - 4}-${currentStartYear - 3}`,
  ];
}


function getStatusLabel(status: string): string {
  if (status === "earnings_paid_status") return "Paid";
  if (status === "earnings_processing_status") return "Processing";
  if (status === "earnings_pending_status") return "Pending";
  return "Pending";
}

function getStatusColor(status: string): string {
  if (status === "earnings_paid_status") return "#333333";
  if (status === "earnings_processing_status") return "#78b517";
  return "#f5aa00";
}

function getAdjustmentIcon(adjustmentType: string): string {
  if (adjustmentType.includes("deduction")) return "\uf56d"; // FA minus-circle
  return "\uf51e"; // FA money-bill-wave (additions)
}

function getAdjustmentLabel(adjustmentType: string): string {
  if (adjustmentType.includes("deduction")) return "Deductions";
  if (adjustmentType.includes("addition")) return "Additions";
  return adjustmentType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatMonthName(isoDate: string, locale: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString(locale, { month: "long" });
}

// ── Component ─────────────────────────────────────────────────────────────────

const Earnings: React.FC = () => {
  const { user } = useUserContext();
  const api = useHttpClient();
  const { t, lang } = useLocalization();
  const navigate = useNavigate();
  const { language } = useParams<{ language: string }>();

  const locale = lang === "ar" ? "ar-EG" : "en-US";

  const [academicYear, setAcademicYear] = useState<string>(getCurrentAcademicYear());
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const yearBtnRef = useRef<HTMLButtonElement>(null);

  const [data, setData] = useState<EarningsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEarnings = async () => {
      if (!user?.userId) return;

      setLoading(true);
      setData(null);
      setError(null);

      try {
        const baseEndpoint = import.meta.env.VITE_GET_EDUCATOR_EARNINGS.replace(
          "{educator_id}",
          user.userId
        );
        const endpoint = `${baseEndpoint}?academic-year=${academicYear}`;
        const response = await api.get(endpoint);
        setData(response.data);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403) {
          setError(t("unauthorized_access_error"));
        } else if (status === 404) {
          setError(t("educator_no_earnings", "No earnings data found."));
        } else {
          setError(t("generic_error", "Something went wrong. Please try again later."));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEarnings();
  }, [user?.userId, academicYear]);

  const currency = data?.educator_currency;
  const classPayments = data?.formatted_class_payments ?? [];
  const adjustments = data?.formatted_educator_adjustments ?? [];
  const monthlyPayments = data?.formatted_monthly_payments ?? [];

  return (
    <main className="earnings-page">
      <div className="container">
        {/* ── Page title ── */}
        <h1 className="earnings-page__title" dir="auto">
          {t("my_earnings_label")}
        </h1>

        {/* ── Academic year filter ── */}
        <div className="earnings-page__filter">
          <button
            ref={yearBtnRef}
            className="earnings-year-filter"
            onClick={() => setYearDropdownOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={yearDropdownOpen}
          >
            <span className="earnings-year-filter__label">{academicYear}</span>
            <span className="earnings-year-filter__icon">
              <i className="fa-regular fa-list" aria-hidden="true" />
            </span>
          </button>
          <Dropdown
            open={yearDropdownOpen}
            anchorRef={yearBtnRef}
            onClose={() => setYearDropdownOpen(false)}
            className="earnings-year-dropdown"
          >
            <ul className="earnings-year-dropdown__list" role="listbox">
              {getAvailableAcademicYears().map((yr) => (
                <li
                  key={yr}
                  role="option"
                  aria-selected={yr === academicYear}
                  className={`earnings-year-dropdown__item${yr === academicYear ? " earnings-year-dropdown__item--active" : ""}`}
                  onClick={() => {
                    setAcademicYear(yr);
                    setYearDropdownOpen(false);
                  }}
                >
                  {yr}
                </li>
              ))}
            </ul>
          </Dropdown>
        </div>

        {/* ── Loading / Error / Empty states ── */}
        {loading && null}
        {!loading && error && (
          <div className="earnings-page__error">
            <p dir="auto">{error}</p>
          </div>
        )}

        {/* ── My Classes ── */}
        {!loading && !error && classPayments.length > 0 && (
          <section className="earnings-page__section">
            <h2 className="earnings-page__section-title" dir="auto">
              {t("my_classes_label")}
            </h2>
            <div className="earnings-classes-grid">
              {classPayments.map((cls) => (
                <div key={cls.class_id} className="earnings-class-card">
                  <div className="earnings-class-card__info">
                    <p className="earnings-class-card__title">{cls.class_title}</p>
                    <p className="earnings-class-card__grade" dir="auto">
                      {t(cls.grade_localization_key, cls.grade_title)}
                    </p>
                    {(cls.schedule_dates ?? []).length > 0 && (
                      <p className="earnings-class-card__schedule">
                        {deriveScheduleString(cls.schedule_dates!, locale)}
                      </p>
                    )}
                  </div>
                  <p className="earnings-class-card__amount" dir="ltr">
                    <span className="earnings-class-card__currency">{currency}</span>
                    {" "}
                    <span className="earnings-class-card__amount-value">
                      {parseFloat(cls.net_amount).toFixed(2)}
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── My Adjustments ── */}
        {!loading && !error && adjustments.length > 0 && (
          <section className="earnings-page__section">
            <h2 className="earnings-page__section-title" dir="auto">
              {t("my_adjustments_label")}
            </h2>
            <div className="earnings-adjustments-row">
              {adjustments.map((adj, idx) => (
                <div key={idx} className="earnings-adjustment-card">
                  <div className="earnings-adjustment-card__left">
                    <span className="earnings-adjustment-card__icon" aria-hidden="true">
                      {getAdjustmentIcon(adj.adjustment_type)}
                    </span>
                    <span className="earnings-adjustment-card__label">
                      {t(adj.adjustment_type, getAdjustmentLabel(adj.adjustment_type))}
                    </span>
                  </div>
                  <p className="earnings-adjustment-card__amount" dir="ltr">
                    <span className="earnings-adjustment-card__currency">{currency}</span>
                    {" "}
                    <span className="earnings-adjustment-card__amount-value">
                      {parseFloat(adj.adjustment_amount).toFixed(2)}
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Earnings by Payment Period ── */}
        {!loading && !error && monthlyPayments.length > 0 && (
          <section className="earnings-page__section">
            <h2 className="earnings-page__section-title" dir="auto">
              {t("earnings_by_month_label")}
            </h2>
            <div className="earnings-period-table">
              <div className="earnings-period-table__head-wrapper">
                <table className="earnings-period-table__element">
                  <colgroup>
                    <col className="earnings-period-table__col--month" />
                    <col className="earnings-period-table__col--amount" />
                    <col className="earnings-period-table__col--status" />
                  </colgroup>
                  <thead className="earnings-period-table__head">
                    <tr className="earnings-period-table__row earnings-period-table__row--head">
                      <th className="earnings-period-table__cell" dir="auto">
                        {t("earnings_month", "Month")}
                      </th>
                      <th className="earnings-period-table__cell earnings-period-table__cell--center" dir="auto">
                        {t("payout_label")} ({currency})
                      </th>
                      <th className="earnings-period-table__cell earnings-period-table__cell--right" dir="auto">
                        {t("status_label")}
                      </th>
                    </tr>
                  </thead>
                </table>
              </div>
              <div className="earnings-period-table__body-wrapper">
                <table className="earnings-period-table__element">
                  <colgroup>
                    <col className="earnings-period-table__col--month" />
                    <col className="earnings-period-table__col--amount" />
                    <col className="earnings-period-table__col--status" />
                  </colgroup>
                  <tbody className="earnings-period-table__body">
                    {monthlyPayments.map((item, idx) => {
                      const d = new Date(item.payment_month);
                      const year = d.getFullYear();
                      const month = d.getMonth() + 1;
                      return (
                        <tr
                          key={idx}
                          className={`earnings-period-table__row earnings-period-table__row--body earnings-period-table__row--clickable${idx % 2 !== 0 ? " earnings-period-table__row--shaded" : ""}`}
                          onClick={() =>
                            navigate(`/${language}/admin/earnings/${year}/${month}`)
                          }
                        >
                          <td className="earnings-period-table__cell" dir="auto">
                            {formatMonthName(item.payment_month, locale)}
                          </td>
                          <td className="earnings-period-table__cell earnings-period-table__cell--center" dir="ltr">
                            {parseFloat(item.total_amount).toFixed(2)}
                          </td>
                          <td
                            className="earnings-period-table__cell earnings-period-table__cell--right"
                            style={{ color: getStatusColor(item.status) }}
                          >
                            {t(item.status, getStatusLabel(item.status))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default Earnings;
